import { UpperCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, Injector, input, output, signal, WritableSignal } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { TranslatePipe } from '@ngx-translate/core';
import moment from 'moment';

@Component({
  selector: 'oyan-ui-chart-filter',
  templateUrl: './chart-filter.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, MatButtonModule, MatDatepickerModule, MatInputModule, UpperCasePipe, FormsModule, ReactiveFormsModule]
})
export class ChartFilterComponent {
  startDateControl = new FormControl(null);
  endDateControl = new FormControl(null);
  defaultChartPeriod = input<number>(0);
  dataRangeChange = output<{ start: string; end: string }>();
  chartPeriod: WritableSignal<number> = signal(0);
  selectedStartDate: WritableSignal<string | null> = signal(null);
  selectedEndDate: WritableSignal<string | null> = signal(null);
  maxStartDate = computed(() => {
    const endDate = this.selectedEndDate();
    const today = new Date();

    if (!endDate) {
      return today;
    }

    const end = moment(endDate).toDate();

    // Start date must be before end date and not exceed today
    return end < today ? end : today;
  });
  maxEndDate = computed(() => {
    const startDate = this.selectedStartDate();
    const today = new Date();

    if (!startDate) {
      return today;
    }

    const start = moment(startDate);
    const oneMonthAfterStart = start.clone().add(1, 'month').toDate();

    // Return the earlier of: one month after start date or today
    return oneMonthAfterStart < today ? oneMonthAfterStart : today;
  });
  private readonly injector = inject(Injector);
  private readonly defaultChartPeriodEffect = effect(
    () => {
      const defaultChartPeriod = this.defaultChartPeriod();
      this.chartPeriod.set(defaultChartPeriod);
    },
    { injector: this.injector }
  );
  private readonly chartPeriodEffect = effect(
    () => {
      const chartPeriod = this.chartPeriod();
      if (chartPeriod) {
        this.dataRangeChange.emit({
          start: moment().subtract(chartPeriod, 'days').toISOString(false),
          end: moment().toISOString(false)
        });
      }
    },
    { injector: this.injector }
  );

  onPeriodClicked(period: number): void {
    this.chartPeriod.set(period);
    this.startDateControl.setValue(null);
    this.endDateControl.setValue(null);
    this.selectedStartDate.set(null);
    this.selectedEndDate.set(null);
  }

  onStartDateChange(date: string): void {
    this.selectedStartDate.set(date);
  }

  onEndDateChange(date: string): void {
    this.selectedEndDate.set(date);
  }

  apply(): void {
    const startDate = this.selectedStartDate();
    const endDate = this.selectedEndDate();

    let ok = true;

    if (!startDate && !endDate) {
      ok = false;
    }

    const start = startDate ? moment(startDate) : null;
    const end = endDate ? moment(endDate) : null;
    const today = moment().startOf('day');

    if (start && start.isAfter(today)) {
      ok = false;
    }
    if (end && end.isAfter(today)) {
      ok = false;
    }
    if (start && end && !end.isSameOrAfter(start)) {
      ok = false;
    }
    if (start && end && end.diff(start, 'days') > 31) {
      ok = false;
    }

    this.chartPeriod.set(0);

    if (ok) {
      this.dataRangeChange.emit({
        start: start?.toISOString(false),
        end: end?.toISOString(false)
      });
    }
  }

  // Historical limit: dates can go back up to 10 years
  private readonly tenYearsAgo = (): Date => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 10);
    return date;
  };

  minDate = computed(() => this.tenYearsAgo());
  minStartDate = computed(() => {
    const endDate = this.selectedEndDate();
    const historicalLimit = this.tenYearsAgo();

    if (!endDate) {
      return historicalLimit;
    }

    const end = moment(endDate);
    const oneMonthBeforeEnd = end.clone().subtract(1, 'month').toDate();

    // Return the later of: one month before end date or 10 years ago
    return oneMonthBeforeEnd > historicalLimit ? oneMonthBeforeEnd : historicalLimit;
  });
  minEndDate = computed(() => {
    const startDate = this.selectedStartDate();
    const historicalLimit = this.tenYearsAgo();

    if (!startDate) {
      return historicalLimit;
    }

    const start = moment(startDate).toDate();

    // End date min is the start date or historical limit, whichever is later
    return start > historicalLimit ? start : historicalLimit;
  });
}
